// Loosely based on https://github.com/text-mask/text-mask

const CARET_TRAP = '[]';

const processCaretTraps = (originalMask) => {
  const indexes = [];
  let mask = [...originalMask];
  let indexOfCaretTrap = mask.indexOf(CARET_TRAP);

  while (indexOfCaretTrap !== -1) {
    indexes.push(indexOfCaretTrap);
    mask = [...mask.slice(0, indexOfCaretTrap), ...mask.slice(indexOfCaretTrap + 1)];
    indexOfCaretTrap = mask.indexOf(CARET_TRAP);
  }

  return { mask, indexes };
};

const convertMaskToPlaceholder = (mask, placeholderChar) =>
  mask
    .map((char) => {
      if (char instanceof RegExp) {
        return placeholderChar;
      }

      return char;
    })
    .join('');

export default function getFormattedValue({
  lastFormattedValue = '',
  mask: originalMask = '',
  placeholderChar = '_',
  value = '',
  skipDeletion = false,
  startCaretPos = 0,
  guide = true,
}) {
  let caretTrapIndexes = [];
  let formattedValue = '';
  let indexOfLastFilledPlaceholderChar = null;
  let mask = originalMask;

  if (typeof mask === 'function') {
    mask = mask(value);
  }

  if (!Array.isArray(mask)) {
    throw new Error('Mask must be an Array.');
  }

  ({ mask, indexes: caretTrapIndexes } = processCaretTraps(mask));

  if (value == null) {
    const placeholder = convertMaskToPlaceholder(mask, placeholderChar);

    return {
      caretTrapIndexes,
      placeholder,
      value: formattedValue,
    };
  }

  const strValue = typeof value === 'number' ? value.toString() : value;
  const placeholder = convertMaskToPlaceholder(mask, placeholderChar);
  const editLength = strValue.length - lastFormattedValue.length;
  const isAddition = editLength > 0;
  const indexOfLastChange = startCaretPos + editLength;
  const charInPlaceholderArr = [];
  const strValueArr = [...strValue].filter((char, index) => {
    if (char === placeholderChar) {
      return false;
    }

    const shouldOffset = index >= indexOfLastChange;

    if (char === placeholder[shouldOffset ? index - editLength : index]) {
      charInPlaceholderArr.push(char);

      return false;
    }

    return true;
  });

  [...placeholder].every((charInPlaceholder, index) => {
    if (!guide && strValueArr.length === 0 && charInPlaceholderArr.length === 0) {
      const placeholderRest = placeholder.substr(index);
      const hasPlaceholderCharLeft = [...placeholderRest].includes(placeholderChar);

      if (!hasPlaceholderCharLeft) {
        formattedValue += placeholderRest;
      }

      return false;
    }

    if (charInPlaceholder === placeholderChar && strValueArr.length) {
      const firstRawCharIndex = strValueArr.findIndex((char) => mask[index].test(char));

      if (firstRawCharIndex > -1) {
        formattedValue += strValueArr[firstRawCharIndex];
        indexOfLastFilledPlaceholderChar = index;

        strValueArr.splice(firstRawCharIndex, 1);
      } else {
        if (guide) {
          formattedValue += placeholderChar;
        }

        strValueArr.splice(0, strValueArr.length);
      }
    } else {
      const charInPlaceholderIndex = charInPlaceholderArr.findIndex(
        (char) => char === charInPlaceholder
      );

      formattedValue += charInPlaceholder;

      if (charInPlaceholderIndex > -1) {
        charInPlaceholderArr.splice(charInPlaceholderIndex, 1);
      }
    }

    return true;
  });

  if (!isAddition && skipDeletion === false) {
    if (indexOfLastFilledPlaceholderChar !== null) {
      const indexOfLastPlaceholderChar = placeholder.lastIndexOf(placeholderChar);
      const formattedStr = formattedValue.substr(indexOfLastPlaceholderChar);
      const rawStr = strValue.substr(-formattedStr.length);

      if (
        !guide &&
        (indexOfLastFilledPlaceholderChar !== indexOfLastPlaceholderChar || formattedStr !== rawStr)
      ) {
        formattedValue = formattedValue.substr(0, indexOfLastFilledPlaceholderChar + 1);
      }
    } else {
      formattedValue = '';
    }
  }

  return {
    caretTrapIndexes,
    placeholder,
    value: formattedValue,
  };
}
